import type { InspectorChange, InspectorResource, StateFinding } from '@/lib/apiTypes'

// ── Resource Inspector ───────────────────────────────────────────────────────
// Interactive view over a version's Terraform resources. The data it renders comes
// from the sanitised /resources endpoint — sensitive attribute values are stripped
// server-side and are NEVER present in the payload (a redacted attribute has no `value`).
export const CHANGE_STYLE: Record<InspectorChange, { color: string; label: string }> = {
  added:     { color: 'var(--green)',   label: '+ added' },
  removed:   { color: 'var(--red)',     label: '− removed' },
  changed:   { color: 'var(--amber)',   label: '~ changed' },
  unchanged: { color: 'var(--text3)',   label: 'unchanged' },
}

/** Module nesting depth: '' = root (0), 'module.vpc' = 1, 'module.vpc.module.subnets' = 2. */
export function moduleDepth(module: string): number {
  return module === '' ? 0 : module.split('.module.').length
}

/** Findings whose resource address maps to this resource (exact, or an indexed instance). */
export function resourceFindings(res: InspectorResource, findings: StateFinding[]): StateFinding[] {
  return findings.filter((f) => f.resource === res.address || (f.resource?.startsWith(res.address + '[') ?? false))
}

/** Deep-clone a value, replacing server redaction sentinels with a safe placeholder string. */
export function maskSentinels(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(maskSentinels)
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>
    if (obj['__redacted'] === true) return '••••••• (sensitive)'
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(obj)) out[k] = maskSentinels(v)
    return out
  }
  return value
}

